<template>
  <article class="meeting card" :class="{ live: isLive }">
    <div class="top">
      <div class="glyph" :class="meeting.mode">
        <i :class="meeting.mode === 'webinar' ? 'ri-presentation-fill' : 'ri-team-fill'"></i>
      </div>
      <div class="badges">
        <span class="badge badge-live" v-if="isLive">Live</span>
        <span class="badge badge-warning" v-else-if="startsSoon"><i class="ri-time-line"></i>{{ untilStart }}</span>
        <span class="badge" v-if="held" :title="`Part of a ${meeting.held_by}: change it there`">
          <i class="ri-user-star-line"></i>{{ heldLabel }}
        </span>
        <span class="badge" :title="meeting.public ? 'Anyone in the school can join' : 'Invited people only'">
          <i :class="meeting.public ? 'ri-global-line' : 'ri-lock-line'"></i>{{ meeting.public ? "Open" : "Invite only" }}
        </span>
      </div>
      <div class="more" ref="more">
        <button class="icon-btn" @click.stop="menu = !menu" aria-label="More actions">
          <i class="ri-more-2-fill"></i>
        </button>
        <Transition name="pop">
          <div class="menu" v-if="menu">
            <button class="menu-item" @click="act('copy')"><i class="ri-link"></i> Copy link</button>
            <p class="menu-note" v-if="mine && held">
              <i class="ri-information-line"></i>Managed by its {{ meeting.held_by }}
            </p>
            <template v-if="mine && !held">
              <button class="menu-item" @click="act('edit')"><i class="ri-edit-line"></i> Edit</button>
              <button class="menu-item" @click="act('people')"><i class="ri-group-line"></i> People</button>
              <div class="menu-sep"></div>
              <button class="menu-item danger" @click="act('delete')"><i class="ri-delete-bin-line"></i> Delete</button>
            </template>
          </div>
        </Transition>
      </div>
    </div>

    <h3 :title="meeting.title">{{ meeting.title }}</h3>
    <p class="desc" v-if="meeting.description">{{ meeting.description }}</p>
    <p class="desc muted" v-else>No description</p>

    <div class="meta">
      <span v-if="meeting.scheduled_at"><i class="ri-calendar-line"></i>{{ scheduled }}</span>
      <span v-else><i class="ri-time-line"></i>Created {{ created }}</span>
      <span v-if="meeting.mode === 'webinar'"><i class="ri-presentation-line"></i>Class / webinar</span>
    </div>

    <footer>
      <div class="by">
        <Avatar :name="creatorName" :image="creator.image" :size="26" />
        <span>{{ mine ? "You" : creatorName }}</span>
      </div>
      <router-link :to="`/${meeting.code}/preview`" class="btn btn-sm" :class="isLive ? 'btn-primary' : 'btn-soft'">
        <i class="ri-vidicon-line"></i>{{ isLive ? "Join now" : "Join" }}
      </router-link>
    </footer>
  </article>
</template>

<script>
import Avatar from "@/components/ui/Avatar.vue";
import { when, ago, until } from "@/format";

export default {
  name: "MeetingCard",
  components: { Avatar },
  emits: ["edit", "people", "delete", "copy"],
  props: {
    meeting: { type: Object, required: true },
    me: { type: String, default: "" },
  },
  data() {
    return { menu: false };
  },
  computed: {
    creator() {
      return typeof this.meeting.creator === "object" && this.meeting.creator ? this.meeting.creator : {};
    },
    creatorName() {
      return `${this.creator.first_name || ""} ${this.creator.last_name || ""}`.trim() || "Someone";
    },
    mine() {
      const id = this.creator.id || this.meeting.creator;
      return !!this.me && id === this.me;
    },
    // A meeting something else owns (a coaching's session) is changed only
    // there; the API refuses it here.
    held() {
      return !!this.meeting.held_by;
    },
    heldLabel() {
      const by = this.meeting.held_by || "";
      return by.charAt(0).toUpperCase() + by.slice(1);
    },
    isLive() {
      return this.meeting.status === "live";
    },
    startsSoon() {
      if (!this.meeting.scheduled_at) return false;
      const ms = new Date(this.meeting.scheduled_at) - Date.now();
      return ms > -15 * 60000 && ms < 24 * 3600000;
    },
    untilStart() {
      return until(this.meeting.scheduled_at) === "now" ? "Starting now" : "Starts " + until(this.meeting.scheduled_at);
    },
    scheduled() {
      return when(this.meeting.scheduled_at);
    },
    created() {
      return ago(this.meeting.created_at);
    },
  },
  mounted() {
    document.addEventListener("click", this.outside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.outside);
  },
  methods: {
    outside(e) {
      if (this.menu && this.$refs.more && !this.$refs.more.contains(e.target)) this.menu = false;
    },
    act(what) {
      this.menu = false;
      this.$emit(what, this.meeting);
    },
  },
};
</script>

<style lang="scss" scoped>
.meeting {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 1.1rem 1.1rem 1rem;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    box-shadow: var(--shadow);
    border-color: var(--line-2);
  }

  &.live {
    border-color: #f3c4c4;
    background: linear-gradient(180deg, #fff7f7, var(--surface) 60%);
  }
}

.top {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.glyph {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: var(--brand-100);
  color: var(--brand);
  font-size: 1.2rem;

  &.webinar {
    background: #ece8fd;
    color: var(--accent);
  }
}

.badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  flex: 1;
}

.more {
  position: relative;

  .menu {
    right: 0;
    top: calc(100% + 4px);
  }
}

h3 {
  margin-top: 0.2rem;
  font-size: 1.02rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.desc {
  font-size: 0.88rem;
  line-height: 1.45;
  color: var(--ink-2);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.55em;

  &.muted {
    color: var(--ink-3);
    font-style: italic;
  }
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1rem;
  font-size: 0.8rem;
  color: var(--ink-3);

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
  }
}

footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.4rem;
  padding-top: 0.8rem;
  border-top: 1px solid var(--line);
}

.by {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  font-size: 0.85rem;
  color: var(--ink-2);

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.menu-note {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  padding: 0.45rem 0.7rem;
  font-size: 0.8rem;
  color: var(--ink-3);
}
</style>
